import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const GRADE_LEVELS = ['Junior 2', 'Junior 3', 'Junior 4', 'Junior 5', 'Junior 6','Middle 1', 'Middle 2', 'Middle 3', 'Senior 1', 'Senior 2']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function generateCode() {
  return `APM-${Math.floor(100 + Math.random() * 900)}`
}

function Avatar({ name, size = 34 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:'var(--accent-bg)', color:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:500, fontSize:size*0.45, flexShrink:0 }}>
      {name?.charAt(0)?.toUpperCase()}
    </div>
  )
}

export default function Students() {
  const [groups, setGroups] = useState([])
  const [students, setStudents] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [loading, setLoading] = useState(true)

  // Group modal
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editGroup, setEditGroup] = useState(null)
  const [groupName, setGroupName] = useState('')
  const [groupGradeLevel, setGroupGradeLevel] = useState('Middle 1')
  const [groupDay, setGroupDay] = useState('Sunday')
  const [groupTime, setGroupTime] = useState('')

  // Student modal
  const [showStudentModal, setShowStudentModal] = useState(false)
  const [editStudent, setEditStudent] = useState(null)
  const [studentName, setStudentName] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [parentEmail, setParentEmail] = useState('')
  const [selectedGrade, setSelectedGrade] = useState('Middle 1')
  const [code, setCode] = useState('')

  useEffect(() => { fetchGroups() }, [])
  useEffect(() => { if (selectedGroup) fetchStudents(selectedGroup.id) }, [selectedGroup])

  async function fetchGroups() {
    const { data } = await supabase
      .from('groups')
      .select('*')
      .order('grade_level')
      .order('name')

    setGroups(data || [])

    if (data?.length && !selectedGroup) {
      setSelectedGroup(data[0])
    }

    setLoading(false)
  }

  async function fetchStudents(groupId) {
    const { data } = await supabase
      .from('students')
      .select('*')
      .eq('group_id', groupId)
      .order('name')

    setStudents(data || [])
  }

  // ---- Groups ----
  function openAddGroup() {
    setEditGroup(null)
    setGroupName('')
    setGroupGradeLevel('Middle 1')
    setGroupDay('Sunday')
    setGroupTime('')
    setShowGroupModal(true)
  }

  function openEditGroup(g, e) {
    e.stopPropagation()
    setEditGroup(g)
    setGroupName(g.name)
    setGroupGradeLevel(g.grade_level || 'Middle 1')
    setGroupDay(g.day || 'Sunday')
    setGroupTime(g.time || '')
    setShowGroupModal(true)
  }

  async function saveGroup() {
    if (!groupName.trim()) return

    const payload = {
      name: groupName.trim(),
      grade_level: groupGradeLevel,
      day: groupDay,
      time: groupTime
    }

    if (editGroup) {
      const { data } = await supabase
        .from('groups')
        .update(payload)
        .eq('id', editGroup.id)
        .select()
        .single()

      setGroups(prev =>
        prev.map(g => g.id === editGroup.id ? data : g)
      )

      if (selectedGroup?.id === editGroup.id) {
        setSelectedGroup(data)
      }
    } else {
      const { data: { user } } = await supabase.auth.getUser()

      const { data } = await supabase
        .from('groups')
        .insert({ ...payload, teacher_id: user.id })
        .select()
        .single()

      setGroups(prev => [...prev, data])
      setSelectedGroup(data)
    }

    setShowGroupModal(false)
  }

  async function deleteGroup(id, e) {
    e.stopPropagation()

    if (!window.confirm('Delete this group and all its students?')) return

    await supabase
      .from('groups')
      .delete()
      .eq('id', id)

    setGroups(prev => prev.filter(g => g.id !== id))

    if (selectedGroup?.id === id) {
      setSelectedGroup(groups.find(g => g.id !== id) || null)
    }
  }

  // ---- Students ----
  function openAddStudent() {
    setEditStudent(null)
    setStudentName('')
    setParentPhone('')
    setParentEmail('')
    setSelectedGrade(selectedGroup?.grade_level || 'Middle 1')
    setCode(generateCode())
    setShowStudentModal(true)
  }

  function openEditStudent(s, e) {
    e.stopPropagation()

    setEditStudent(s)
    setStudentName(s.name)
    setParentPhone(s.parent_phone || '')
    setParentEmail(s.parent_email || '')
    setSelectedGrade(s.grade || 'Middle 1')
    setCode(s.student_code || '')
    setShowStudentModal(true)
  }

  async function saveStudent() {
    if (!studentName.trim()) return

    // تحقق إن grade الطالب يتطابق مع grade المجموعة
    if (
      !editStudent &&
      selectedGroup?.grade_level &&
      selectedGrade !== selectedGroup.grade_level
    ) {
      alert(
        `⚠️ Grade mismatch!\n\nThis group is for "${selectedGroup.grade_level}" students.\nYou selected "${selectedGrade}" for the student.\n\nPlease change the student's grade to match the group.`
      )
      return
    }

    const payload = {
      name: studentName.trim(),
      parent_phone: parentPhone,
      parent_email: parentEmail,
      grade: selectedGrade,
      student_code: code
    }

    if (editStudent) {
      const { data } = await supabase
        .from('students')
        .update(payload)
        .eq('id', editStudent.id)
        .select()
        .single()

      setStudents(prev =>
        prev.map(s => s.id === editStudent.id ? data : s)
      )
    } else {
      const { data, error } = await supabase
        .from('students')
        .insert({
          ...payload,
          group_id: selectedGroup.id
        })
        .select()
        .single()

      if (error) {
        alert(error.message)
        return
      }

      setStudents(prev => [...prev, data])
    }

    setShowStudentModal(false)
  }

  async function deleteStudent(id, e) {
    e.stopPropagation()

    if (!window.confirm('Delete this student and all their data?')) return

    const { error } = await supabase
      .from('students')
      .delete()
      .eq('id', id)

    if (error) {
      alert(error.message)
      return
    }

    setStudents(prev => prev.filter(s => s.id !== id))
  }

  // Group by grade level
  const groupedGroups = GRADE_LEVELS
    .map(level => ({
      level,
      items: groups.filter(g => g.grade_level === level)
    }))
    .filter(g => g.items.length > 0)

  const ungrouped = groups.filter(g => !g.grade_level)

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  return (
    <div>

      <div
        style={{
          display:'flex',
          justifyContent:'space-between',
          alignItems:'center',
          marginBottom:20
        }}
      >
        <h2 style={{ fontSize:20, fontWeight:500 }}>
          Students
        </h2>

        <button
          className="btn btn-primary"
          onClick={openAddGroup}
        >
          + New Group
        </button>
      </div>

      {/* Groups sidebar + students */}
      <div style={{ display:'flex', gap:20 }}>

        {/* Groups List */}
        <div style={{ width:200, flexShrink:0 }}>

          {groupedGroups.map(({ level, items }) => (
            <div key={level} style={{ marginBottom:16 }}>

              <div
                style={{
                  fontSize:11,
                  fontWeight:600,
                  color:'var(--text3)',
                  textTransform:'uppercase',
                  letterSpacing:.05,
                  marginBottom:6,
                  paddingLeft:4
                }}
              >
                {level}
              </div>

              {items.map(g => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroup(g)}
                  style={{
                    padding:'9px 12px',
                    borderRadius:8,
                    marginBottom:4,
                    cursor:'pointer',
                    background:
                      selectedGroup?.id === g.id
                        ? 'var(--accent-bg)'
                        : 'var(--surface)',
                    border:
                      `0.5px solid ${
                        selectedGroup?.id === g.id
                          ? 'var(--accent)'
                          : 'var(--border)'
                      }`,
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'space-between',
                    gap:8
                  }}
                >

                  <div>
                    <div
                      style={{
                        fontSize:13,
                        fontWeight:500,
                        color:
                          selectedGroup?.id === g.id
                            ? 'var(--accent)'
                            : 'var(--text)'
                      }}
                    >
                      {g.name}
                    </div>

                    {(g.day || g.time) && (
                      <div
                        style={{
                          fontSize:11,
                          color:'var(--text3)',
                          marginTop:2
                        }}
                      >
                        {g.day} {g.time && `· ${g.time}`}
                      </div>
                    )}
                  </div>

                  <div style={{ display:'flex', gap:4 }}>

                    <button
                      onClick={e => openEditGroup(g, e)}
                      style={{
                        background:'none',
                        border:'none',
                        cursor:'pointer',
                        fontSize:14,
                        padding:2
                      }}
                    >
                      ✏️
                    </button>

                    <button
                      onClick={e => deleteGroup(g.id, e)}
                      style={{
                        background:'none',
                        border:'none',
                        cursor:'pointer',
                        fontSize:14,
                        padding:2
                      }}
                    >
                      🗑
                    </button>

                  </div>
                </div>
              ))}

            </div>
          ))}

          {ungrouped.length > 0 && (
            <div style={{ marginBottom:16 }}>

              <div
                style={{
                  fontSize:11,
                  fontWeight:600,
                  color:'var(--text3)',
                  textTransform:'uppercase',
                  marginBottom:6,
                  paddingLeft:4
                }}
              >
                Other
              </div>

              {ungrouped.map(g => (
                <div
                  key={g.id}
                  onClick={() => setSelectedGroup(g)}
                  style={{
                    padding:'9px 12px',
                    borderRadius:8,
                    marginBottom:4,
                    cursor:'pointer',
                    background:
                      selectedGroup?.id === g.id
                        ? 'var(--accent-bg)'
                        : 'var(--surface)',
                    border:
                      `0.5px solid ${
                        selectedGroup?.id === g.id
                          ? 'var(--accent)'
                          : 'var(--border)'
                      }`,
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'space-between'
                  }}
                >

                  <span
                    style={{
                      fontSize:13,
                      fontWeight:500,
                      color:
                        selectedGroup?.id === g.id
                          ? 'var(--accent)'
                          : 'var(--text)'
                    }}
                  >
                    {g.name}
                  </span>

                  <div style={{ display:'flex', gap:4 }}>

                    <button
                      onClick={e => openEditGroup(g, e)}
                      style={{
                        background:'none',
                        border:'none',
                        cursor:'pointer',
                        fontSize:14
                      }}
                    >
                      ✏️
                    </button>

                    <button
                      onClick={e => deleteGroup(g.id, e)}
                      style={{
                        background:'none',
                        border:'none',
                        cursor:'pointer',
                        fontSize:14
                      }}
                    >
                      🗑
                    </button>

                  </div>
                </div>
              ))}

            </div>
          )}

          {groups.length === 0 && (
            <div
              style={{
                color:'var(--text3)',
                fontSize:13,
                textAlign:'center',
                padding:16
              }}
            >
              No groups yet
            </div>
          )}

        </div>

        {/* Students */}
        <div style={{ flex:1 }}>

          {!selectedGroup ? (
            <div className="empty">
              Select or create a group
            </div>
          ) : (
            <>

              <div
                style={{
                  display:'flex',
                  justifyContent:'space-between',
                  alignItems:'center',
                  marginBottom:16
                }}
              >

                <div>

                  <span
                    style={{
                      fontWeight:500,
                      fontSize:16
                    }}
                  >
                    {selectedGroup.name}
                  </span>

                  {selectedGroup.grade_level && (
                    <span
                      className="badge badge-accent"
                      style={{ marginLeft:8 }}
                    >
                      {selectedGroup.grade_level}
                    </span>
                  )}

                  {selectedGroup.day && (
                    <span
                      style={{
                        fontSize:12,
                        color:'var(--text3)',
                        marginLeft:8
                      }}
                    >
                      {selectedGroup.day}{' '}
                      {selectedGroup.time && `· ${selectedGroup.time}`}
                    </span>
                  )}

                </div>

                <button
                  className="btn btn-primary"
                  onClick={openAddStudent}
                >
                  + Add Student
                </button>

              </div>

              {students.length === 0 ? (
                <div className="empty">
                  No students in this group
                </div>
              ) : (
                <>

                  {/* Desktop table */}
                  <div className="desktop-table">

                    <div
                      className="card"
                      style={{
                        padding:0,
                        overflow:'hidden'
                      }}
                    >

                      <table>

                        <thead>
                          <tr>
                            <th>Student</th>
                            <th>Grade</th>
                            <th>Code</th>
                            <th>Parent Phone</th>
                            <th></th>
                          </tr>
                        </thead>

                        <tbody>

                          {students.map(s => (
                            <tr key={s.id}>

                              <td>

                                <div
                                  style={{
                                    display:'flex',
                                    alignItems:'center',
                                    gap:10
                                  }}
                                >

                                  <div
                                    style={{
                                      width:34,
                                      height:34,
                                      borderRadius:'50%',
                                      background:'var(--accent-bg)',
                                      color:'var(--accent)',
                                      display:'flex',
                                      alignItems:'center',
                                      justifyContent:'center',
                                      fontWeight:500
                                    }}
                                  >
                                    {s.name.charAt(0).toUpperCase()}
                                  </div>

                                  <div>

                                    <div
                                      style={{
                                        fontWeight:500
                                      }}
                                    >
                                      {s.name}
                                    </div>

                                    {s.parent_email && (
                                      <div
                                        style={{
                                          fontSize:12,
                                          color:'var(--text3)'
                                        }}
                                      >
                                        {s.parent_email}
                                      </div>
                                    )}

                                  </div>

                                </div>

                              </td>

                              <td>
                                {s.grade && (
                                  <span className="badge badge-accent">
                                    {s.grade}
                                  </span>
                                )}
                              </td>

                              <td>
                                <span
                                  style={{
                                    fontFamily:'monospace',
                                    fontWeight:600,
                                    color:'var(--accent)'
                                  }}
                                >
                                  {s.student_code}
                                </span>
                              </td>

                              <td
                                style={{
                                  color:'var(--text3)'
                                }}
                              >
                                {s.parent_phone || '—'}
                              </td>

                              <td>

                                <div
                                  style={{
                                    display:'flex',
                                    gap:6
                                  }}
                                >

                                  <button
                                    className="btn btn-sm"
                                    onClick={e => openEditStudent(s, e)}
                                  >
                                    ✏️ Edit
                                  </button>

                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={e => deleteStudent(s.id, e)}
                                  >
                                    Delete
                                  </button>

                                </div>

                              </td>

                            </tr>
                          ))}

                        </tbody>

                      </table>

                    </div>

                  </div>

                  {/* Mobile cards */}
                  <div className="mobile-cards">

                    {students.map(s => (
                      <div
                        key={s.id}
                        className="mobile-card"
                      >

                        <div className="mobile-card-row">

                          <div
                            style={{
                              display:'flex',
                              alignItems:'center',
                              gap:10
                            }}
                          >

                            <div
                              style={{
                                width:40,
                                height:40,
                                borderRadius:'50%',
                                background:'var(--accent-bg)',
                                color:'var(--accent)',
                                display:'flex',
                                alignItems:'center',
                                justifyContent:'center',
                                fontWeight:600,
                                fontSize:17,
                                flexShrink:0
                              }}
                            >
                              {s.name.charAt(0).toUpperCase()}
                            </div>

                            <div>

                              <div
                                style={{
                                  fontWeight:600,
                                  fontSize:15
                                }}
                              >
                                {s.name}
                              </div>

                              {s.grade && (
                                <span
                                  className="badge badge-accent"
                                  style={{
                                    marginTop:4,
                                    display:'inline-flex'
                                  }}
                                >
                                  {s.grade}
                                </span>
                              )}

                            </div>

                          </div>

                          <div
                            style={{
                              textAlign:'right'
                            }}
                          >

                            <div
                              style={{
                                fontSize:10,
                                color:'var(--text3)'
                              }}
                            >
                              Code
                            </div>

                            <div
                              style={{
                                fontFamily:'monospace',
                                fontWeight:700,
                                color:'var(--accent)'
                              }}
                            >
                              {s.student_code}
                            </div>

                          </div>

                        </div>

                        {s.parent_phone && (
                          <div
                            style={{
                              fontSize:13,
                              color:'var(--text3)',
                              margin:'8px 0 0'
                            }}
                          >
                            📞 {s.parent_phone}
                          </div>
                        )}

                        <div
                          style={{
                            display:'flex',
                            gap:8,
                            marginTop:10
                          }}
                        >

                          <button
                            className="btn btn-sm"
                            style={{
                              flex:1,
                              justifyContent:'center'
                            }}
                            onClick={e => openEditStudent(s, e)}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            style={{
                              flex:1,
                              justifyContent:'center'
                            }}
                            onClick={e => deleteStudent(s.id, e)}
                          >
                            Delete
                          </button>

                        </div>

                      </div>
                    ))}

                  </div>

                </>
              )}

            </>
          )}

        </div>

      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <div
          className="overlay"
          onClick={e =>
            e.target === e.currentTarget &&
            setShowGroupModal(false)
          }
        >

          <div className="modal">

            <div className="modal-title">
              {editGroup ? 'Edit Group' : 'New Group'}
            </div>

            <div className="field">

              <label>Group Name</label>

              <input
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="e.g. Group A"
                autoFocus
              />

            </div>

            <div className="field">

              <label>Grade Level</label>

              <div
                style={{
                  display:'flex',
                  gap:8,
                  flexWrap:'wrap'
                }}
              >

                {GRADE_LEVELS.map(g => (
                  <button
                    key={g}
                    className={`chip ${
                      groupGradeLevel === g ? 'active' : ''
                    }`}
                    onClick={() => setGroupGradeLevel(g)}
                  >
                    {g}
                  </button>
                ))}

              </div>

            </div>

            <div className="field">

              <label>Day</label>

              <select
                value={groupDay}
                onChange={e => setGroupDay(e.target.value)}
              >
                {DAYS.map(d => (
                  <option key={d}>{d}</option>
                ))}
              </select>

            </div>

            <div className="field">
              <label>Time (optional)</label>
              <div style={{ display:'flex', gap:8 }}>
                <select
                  value={groupTime.split(' ')[0] || '12:00'}
                  onChange={e => setGroupTime(e.target.value + ' ' + (groupTime.split(' ')[1] || 'AM'))}
                  style={{ flex:1 }}>
                  {['12','1','2','3','4','5','6','7','8','9','10','11'].map(h =>
                    ['00','15','30','45'].map(m => (
                      <option key={h+':'+m} value={`${h}:${m}`}>{h}:{m}</option>
                    ))
                  )}
                </select>
                <select
                  value={groupTime.split(' ')[1] || 'AM'}
                  onChange={e => setGroupTime((groupTime.split(' ')[0] || '12:00') + ' ' + e.target.value)}
                  style={{ width:70 }}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
            
            <div className="modal-actions">

              <button
                className="btn"
                onClick={() => setShowGroupModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={saveGroup}
              >
                {editGroup ? 'Save Changes' : 'Add Group'}
              </button>

            </div>

          </div>

        </div>
      )}

      {/* Student Modal */}
      {showStudentModal && (
        <div
          className="overlay"
          onClick={e =>
            e.target === e.currentTarget &&
            setShowStudentModal(false)
          }
        >

          <div className="modal">

            <div className="modal-title">
              {editStudent ? 'Edit Student' : 'New Student'}
            </div>

            {!editStudent && (
              <div
                style={{
                  background:'var(--accent-bg)',
                  borderRadius:10,
                  padding:'12px 16px',
                  textAlign:'center',
                  marginBottom:16
                }}
              >

                <div
                  style={{
                    fontSize:12,
                    color:'var(--accent)',
                    marginBottom:4
                  }}
                >
                  Student Code — Share with parent
                </div>

                <div
                  style={{
                    fontSize:26,
                    fontWeight:700,
                    color:'var(--accent)',
                    letterSpacing:2
                  }}
                >
                  {code}
                </div>

                <button
                  onClick={() => setCode(generateCode())}
                  style={{
                    background:'none',
                    border:'none',
                    color:'var(--text3)',
                    fontSize:12,
                    marginTop:6,
                    cursor:'pointer'
                  }}
                >
                  🔄 Generate new
                </button>

              </div>
            )}

            {editStudent && (
              <div
                style={{
                  background:'var(--accent-bg)',
                  borderRadius:10,
                  padding:'10px 16px',
                  marginBottom:16,
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'space-between'
                }}
              >

                <span
                  style={{
                    fontSize:12,
                    color:'var(--accent)'
                  }}
                >
                  Student Code
                </span>

                <span
                  style={{
                    fontFamily:'monospace',
                    fontWeight:700,
                    color:'var(--accent)',
                    fontSize:16,
                    letterSpacing:1
                  }}
                >
                  {code}
                </span>

              </div>
            )}

            <div className="field">

              <label>Student Name</label>

              <input
                value={studentName}
                onChange={e => setStudentName(e.target.value)}
                placeholder="Full name"
                autoFocus
              />

            </div>

            <div className="field">

              <label>Academic Year</label>

              <div
                style={{
                  display:'flex',
                  gap:8,
                  flexWrap:'wrap'
                }}
              >

                {GRADE_LEVELS.map(g => (
                  <button
                    key={g}
                    className={`chip ${
                      selectedGrade === g ? 'active' : ''
                    }`}
                    onClick={() => setSelectedGrade(g)}
                  >
                    {g}
                  </button>
                ))}

              </div>

            </div>
{/* 
            <div className="field">

              <label>Parent Phone</label>

              <input
                value={parentPhone}
                onChange={e => setParentPhone(e.target.value)}
                placeholder="01xxxxxxxxx"
                type="tel"
              />

            </div>

            <div className="field">

              <label>Parent Email</label>

              <input
                value={parentEmail}
                onChange={e => setParentEmail(e.target.value)}
                placeholder="parent@email.com"
                type="email"
              />

            </div> */}

            <div className="modal-actions">

              <button
                className="btn"
                onClick={() => setShowStudentModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={saveStudent}
              >
                {editStudent ? 'Save Changes' : 'Add Student'}
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  )
}